import React, { useState } from 'react';
import { UserIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';

const ClientInformationForm = ({ clientInfo, onClientInfoChange, onSubmit, loading }) => {
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!clientInfo.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!clientInfo.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!clientInfo.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientInfo.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!clientInfo.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s\-()]+$/.test(clientInfo.phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit();
    }
  };

  const handleChange = (field, value) => {
    onClientInfoChange({ ...clientInfo, [field]: value });
    // Clear error for this field when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Information</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="firstName"
                value={clientInfo.firstName || ''}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-lg
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.firstName ? 'border-red-500' : 'border-gray-300'}
                `}
                placeholder="John"
              />
            </div>
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <UserIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="lastName"
                value={clientInfo.lastName || ''}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-lg
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                  ${errors.lastName ? 'border-red-500' : 'border-gray-300'}
                `}
                placeholder="Doe"
              />
            </div>
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <EnvelopeIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              value={clientInfo.email || ''}
              onChange={(e) => handleChange('email', e.target.value)}
              className={`
                block w-full pl-10 pr-3 py-2 border rounded-lg
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                ${errors.email ? 'border-red-500' : 'border-gray-300'}
              `}
              placeholder="john.doe@example.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <PhoneIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="tel"
              id="phone"
              value={clientInfo.phone || ''}
              onChange={(e) => handleChange('phone', e.target.value)}
              className={`
                block w-full pl-10 pr-3 py-2 border rounded-lg
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500
                ${errors.phone ? 'border-red-500' : 'border-gray-300'}
              `}
              placeholder="+1 (555) 123-4567"
            />
          </div>
          {errors.phone && (
            <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Additional Notes (Optional)
          </label>
          <textarea
            id="notes"
            rows={3}
            value={clientInfo.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Any special requests or health concerns we should know about?"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`
            w-full py-3 px-4 rounded-lg font-medium text-white
            transition-colors
            ${loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'
            }
          `}
        >
          {loading ? 'Processing...' : 'Confirm Booking'}
        </button>

        <p className="text-xs text-gray-500 text-center mt-2">
          * Required fields
        </p>
      </form>
    </div>
  );
};

export default ClientInformationForm;
